// This is a react-hook-form resolver for Yup form validation
// Most of this code comes from https://github.com/react-hook-form/resolvers/

/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  appendErrors,
  Field,
  FieldError,
  FieldErrors,
  FieldValues,
  get,
  ResolverOptions,
  ResolverResult,
  set,
  UnpackNestedValue,
} from "react-hook-form";

import * as Yup from "yup";
import type Lazy from "yup/lib/Lazy";

type Options<T extends Yup.AnyObjectSchema | Lazy<any>> = Parameters<
  T["validate"]
>[1];

export type Resolver = <T extends Yup.AnyObjectSchema | Lazy<any>>(
  schema: T,
  schemaOptions?: Options<T>,
  factoryOptions?: { mode?: "async" | "sync" },
) => <TFieldValues extends FieldValues, TContext>(
  values: UnpackNestedValue<TFieldValues>,
  context: TContext | undefined,
  options: ResolverOptions<TFieldValues>,
) => Promise<ResolverResult<TFieldValues>>;

export const toNestError = <TFieldValues extends FieldValues>(
  errors: Record<string, FieldError>,
  options: ResolverOptions<TFieldValues>,
): FieldErrors<TFieldValues> => {
  if (options.shouldUseNativeValidation) {
    validateFieldsNatively(errors, options);
  }

  const fieldErrors: FieldErrors<TFieldValues> =
    {} as FieldErrors<TFieldValues>;
  for (const path in errors) {
    const field = get(options.fields, path) as Field["_f"] | undefined;

    set(
      fieldErrors,
      path,
      Object.assign(errors[path], { ref: field && field.ref }),
    );
  }

  return fieldErrors;
};

// Native validation (web only)
export const validateFieldsNatively = <TFieldValues extends FieldValues>(
  errors: Record<string, FieldError>,
  options: ResolverOptions<TFieldValues>,
): void => {
  for (const fieldPath in options.fields) {
    const field = options.fields[fieldPath];

    if (field && field.ref && "reportValidity" in field.ref) {
      const error = get(errors, fieldPath) as FieldError | undefined;

      field.ref.setCustomValidity((error && error.message) || "");

      field.ref.reportValidity();
    }
  }
};

/**
 * Why `path!` ? because it could be `undefined` in some case
 * https://github.com/jquense/yup#validationerrorerrors-string--arraystring-value-any-path-string
 */
const parseErrorSchema = (
  error: Yup.ValidationError,
  validateAllFieldCriteria: boolean,
) => {
  return error.inner.reduce<Record<string, FieldError>>((previous, error) => {
    if (!previous[error.path!]) {
      previous[error.path!] = { message: error.message, type: error.type! };
    }

    if (validateAllFieldCriteria) {
      const types = previous[error.path!].types;
      const messages = types && types[error.type!];

      previous[error.path!] = appendErrors(
        error.path!,
        validateAllFieldCriteria,
        previous,
        error.type!,
        messages
          ? ([] as string[]).concat(messages as string[], error.message)
          : error.message,
      ) as FieldError;
    }

    return previous;
  }, {});
};

const yupResolver: Resolver =
  (schema, schemaOptions = {}, resolverOptions = {}) =>
  async (values, context, options) => {
    try {
      if (schemaOptions.context && import.meta.env.DEV) {
        console.warn(
          "You should not used the yup options context. Please, use the 'useForm' context object instead",
        );
      }

      const result = await schema[
        resolverOptions.mode === "sync" ? "validateSync" : "validate"
      ](
        values,
        Object.assign({ abortEarly: false }, schemaOptions, { context }),
      );

      if (options.shouldUseNativeValidation) {
        validateFieldsNatively({}, options);
      }

      return {
        values: result,
        errors: {},
      };
    } catch (e) {
      return {
        values: {},
        errors: toNestError(
          parseErrorSchema(
            e as Yup.ValidationError,
            !options.shouldUseNativeValidation &&
              options.criteriaMode === "all",
          ),
          options,
        ),
      };
    }
  };

export default yupResolver;
